/* eslint-disable @typescript-eslint/no-shadow */
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React, {
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import { components, ControlProps } from 'react-select';
import { uniqueId, debounce } from 'lodash';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { styled } from '@mui/system';
import CustomSelect from '../../../components/CustomSelect';
import { useLazyGetAreaByNameQuery } from '../../../slices/area/apis/areaApi';
import { clearPoints } from '../../../slices/area/slices/searchFlightFormSlice';
import { useAppDispatch, useAppSelector } from '../../../utils/hook';
import SearchFlightResultItem from '../SearchFlightResultItem';
import type { IFlightResultItem } from '../SearchFlightResultItem/index.d';
import type { SearchInputDialogProps } from './index.d';
import { setCurrentArea } from '../../../slices/area/slices/areaSlice';
import { style } from '@/styles/globals';
import { NoResultSearchIcon, SearchIcon } from 'public/icons';
import { pr } from '@/utils/pxToRem';
import { transformAreaData } from '@/utils/transformAreaData';
import { LOCATION_TYPE, LOCATION_TYPE_PRIORITY } from '@/utils/constants';

const StyleLoading = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-top: 50%;
  align-items: center;
`;
const StyledStack = styled(Stack)`
  .reactSelectControl {
    border-color: transparent;
    border-radius: 47px;
    background-color: ${style.color.white2};
    padding: 8px 12px;
  }
  .reactSelectControl > div:first-of-type > div:first-of-type {
    color: black;
  }
`;

const Control = ({ children, ...props }: ControlProps<any, true>) => (
  <components.Control
    {...props}
    className="reactSelectControl"
  >
    <SearchIcon />
    {children}
  </components.Control>
);

const SearchInputDialog: React.FC<SearchInputDialogProps> = ({
  type,
  setIsInputEmpty,
  setClose,
}) => {
  const dispatch = useAppDispatch();
  const { startPointHome, toPointHome } = useAppSelector((state) => state.searchFlightForm);
  const areaPoint = type === 'from' ? startPointHome : toPointHome;

  const isInputEmptyRef = useRef(true);

  // const areaPoint = useAppSelector((state) => (type === 'from'
  //   ? state.searchFlightForm.startPoint
  //   : state.searchFlightForm.toPoint));

  const dataMapping = useRef<IFlightResultItem[]>([]);
  const [getAreaByName, {
    data, isFetching, error,
  }] = useLazyGetAreaByNameQuery();

  const debounceGetArea = useRef(debounce(getAreaByName, 200));
  const getTitleKeyByType = useCallback((locationType: number) => {
    if (locationType === LOCATION_TYPE.AIRPORT_STATION) return 'Sân bay';
    if (locationType === LOCATION_TYPE.CITY) return 'Tỉnh/Thành phố';
    if (locationType === LOCATION_TYPE.DISTRICT) return 'Quận/Huyện';
    if (locationType === LOCATION_TYPE.WARD) return 'Phường/Xã';
    return 'Khu vực khác';
  }, []);

  const onExit = () => {
    dispatch(setCurrentArea({ currentArea: '' }));
    isInputEmptyRef.current = true;
    setIsInputEmpty(true);
  };
  dataMapping.current = data && (!isInputEmptyRef.current || areaPoint)
    ? transformAreaData(data)
    : [];

  const groupedData = useMemo(() => {
    if (!dataMapping.current || dataMapping.current.length === 0) {
      return [] as Array<{ type: number, title: string, items: IFlightResultItem[] }>;
    }
    const grouped = dataMapping.current.reduce((acc, item) => {
      const t = item.type;
      if (!acc[t]) {
        acc[t] = [] as IFlightResultItem[];
      }
      acc[t].push(item);
      return acc;
    }, {} as Record<number, IFlightResultItem[]>);
    return LOCATION_TYPE_PRIORITY
      .filter((t) => grouped[t] && grouped[t].length > 0)
      .map((t) => ({
        type: t,
        title: getTitleKeyByType(t),
        items: grouped[t],
      }));
  }, [dataMapping.current, getTitleKeyByType]);

  const handleInputChange = (value: string) => {
    if (value.length === 0) {
      isInputEmptyRef.current = true;
      setIsInputEmpty(true);
      return;
    } if (value.length > 50) {
      return;
    }
    if (isInputEmptyRef.current) {
      setIsInputEmpty(false);
    }
    isInputEmptyRef.current = false;
    debounceGetArea.current(value, true);
  };

  useEffect(() => {
    if (!areaPoint) {
      setIsInputEmpty(true);
      isInputEmptyRef.current = true;
    }
  }, [areaPoint]);

  // Handle quick fill from merge notice (desktop/mobile interoperability)
  useEffect(() => {
    const handler = (e: any) => {
      const value = e?.detail || '';
      if (!value) return;
      isInputEmptyRef.current = false;
      setIsInputEmpty(false);
      try {
        dispatch(setCurrentArea({ currentArea: value }));
        debounceGetArea.current(value, true);
      } catch (err) {
        // no-op
      }
    };
    window.addEventListener('search.quickFill', handler as EventListener);
    return () => {
      window.removeEventListener('search.quickFill', handler as EventListener);
    };
  }, [dispatch]);

  // useEffect(() => {
  //   if (areaPoint) {
  //     getAreaByName(areaPoint.location, true);
  //   }
  // }, []);

  return (
    <StyledStack>
      <CustomSelect
        value={null}
        Control={Control}
        locationType={type}
        onExit={onExit}
        onChange={(newValue, actionMeta) => {
          if (actionMeta.action === 'clear') {
            dispatch(clearPoints({ type }));
            isInputEmptyRef.current = true;
          }
        }}
        onInputChange={(value) => handleInputChange(value)}
      />

      {error && 'status' in error && <div>{(error.data as any).message}</div>}

      {/* {!isFetching && dataMapping.current.length > 0 ? (
        <Typography
          sx={{ mt: 1, mb: 1 }}
          variant="body1"
        >
          Kết quả tìm thấy
        </Typography>
      ) : null} */}
      {!isInputEmptyRef.current && !isFetching
        && groupedData.map((group) => (
          <React.Fragment key={`group-${group.type}`}>
            <GroupTitle>
              <GroupTitleText variant="body2">
                {group.title}
              </GroupTitleText>
            </GroupTitle>
            {group.items.map((item) => (
              <SearchFlightResultItem
                type={type}
                setClose={setClose}
                isSearchResult
                key={uniqueId()}
                data={item}
              />
            ))}
          </React.Fragment>
        ))}
      {isFetching && (
        <StyleLoading>
          <Box sx={{
            display: 'flex',
            pb: pr`16px`,
          }}
          >
            <CircularProgress color="inherit" />
          </Box>
          <Typography variant="body2">
            Vui lòng đợi. Tìm kiếm của bạn đang được xử lý
          </Typography>
        </StyleLoading>
      )}
      {data?.length === 0 && !isInputEmptyRef.current && !isFetching && (
        <StyleLoading>
          <Box sx={{
            display: 'flex',
            pb: pr`16px`,
          }}
          >
            <NoResultSearchIcon />
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: style.color.black2,
              fontWeight: '700',
            }}
          >
            Rất tiếc Goyolo không tìm thấy địa điểm này
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: style.color.black2,
              fontSize: '12px',
              lineHeight: '16px',
              mt: '8px',
            }}
          >
            Bạn vui lòng nhập tỉnh/thành phố, quận/huyện, sân bay để tìm.
          </Typography>
        </StyleLoading>
      )}
    </StyledStack>
  );
};

export default SearchInputDialog;

const GroupTitle = styled('div')`
  background-color: #f8f9fa;
  border-bottom: 1px solid #e8edf1;
  position: sticky;
  top: 0px;
  z-index: 1;
`;

const GroupTitleText = styled(Typography)`
  color: ${style.color.black2};
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  padding: ${pr`8px`} ${pr`16px`};
`;
